import React, { useState } from "react";
import { motion } from "framer-motion";
import { FaRobot, FaBrain, FaUsers, FaCode, FaPalette, FaChartLine } from "react-icons/fa";

export function DualCaseStudies() {
  const [activeCase, setActiveCase] = useState('omnigeist');

  const caseStudies = {
    omnigeist: {
      title: "Case Study: Omnigeist Platform Development",
      subtitle: "AI-Enhanced Enterprise Development",
      description: "A year-long journey building a complex narrative platform using AI-augmented development",
      capabilities: [
        {
          category: "Architecture & Development",
          icon: FaCode,
          tools: [{
            name: "Full-Stack Implementation",
            description: "Leveraging Claude Opus, GPT-4, and Semantic Kernel with .NET 8, Blazor, Entity Framework",
            metrics: "Complex platform built by one developer"
          }],
          color: "text-cyan-500"
        },
        {
          category: "AI Integration",
          icon: FaBrain,
          tools: [{
            name: "Advanced AI Systems",
            description: "Deep integration of multiple AI services, custom agents, and automation workflows",
            metrics: "Seamless AI-human collaboration"
          }],
          color: "text-purple-500"
        },
        {
          category: "Project Scale",
          icon: FaChartLine,
          tools: [{
            name: "Enterprise-Grade Development",
            description: "Complete narrative platform with complex data models and AI processing",
            metrics: "12-month development cycle"
          }],
          color: "text-green-500"
        }
      ]
    },
    website: {
      title: "Case Study: Corporate Website Development",
      subtitle: "Rapid AI-Powered Deployment",
      description: "From concept to live website in 10 days using AI-accelerated development",
      capabilities: [
        {
          category: "Development Acceleration",
          icon: FaCode,
          tools: [{
            name: "Rapid Implementation",
            description: "Using Cursor, Windsurf, Github Copilot,and AI-powered development tools for fast, efficient coding",
            metrics: "Complete website in 10 days"
          }],
          color: "text-cyan-500"
        },
        {
          category: "Creative & Design",
          icon: FaPalette,
          tools: [{
            name: "AI-Powered Content Creation",
            description: "Claude Opus, GPT-4, Midjourney, Sora and other Multimedia Generative Tools for rapid content and design generation",
            metrics: "4-hour design iterations"
          }],
          color: "text-purple-500"
        },
        {
          category: "Process Optimization",
          icon: FaChartLine,
          tools: [{
            name: "Streamlined Workflow",
            description: "AI tools for planning, content creation, and deployment optimization. Agentic project and product management",
            metrics: "90% time reduction vs traditional"
          }],
          color: "text-green-500"
        }
      ]
    }
  };

  const activeStudy = caseStudies[activeCase];

  return (
    <section id="case-studies" className="relative scroll-mt-24 bg-gray-900 py-24">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-2xl lg:text-center mb-8">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center rounded-full bg-blue-400/10 px-4 py-1.5 text-sm font-medium text-blue-400 ring-1 ring-inset ring-blue-400/20"
          >
            Case Studies
          </motion.h2>
          
          <div className="flex justify-center gap-4 mt-4">
            <button 
              onClick={() => setActiveCase('omnigeist')}
              className={`px-4 py-2 rounded-full transition-all ${
                activeCase === 'omnigeist' 
                  ? 'bg-primary text-white' 
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              Omnigeist Platform
            </button>
            <button 
              onClick={() => setActiveCase('website')}
              className={`px-4 py-2 rounded-full transition-all ${
                activeCase === 'website' 
                  ? 'bg-primary text-white' 
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              Corporate Website
            </button>
          </div>

          <motion.p 
            key={activeStudy.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl"
          >
            {activeStudy.subtitle}
          </motion.p>
          <motion.p 
            key={activeStudy.description}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-6 text-lg leading-8 text-gray-300"
          >
            {activeStudy.description}
          </motion.p>
        </div>

        <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
          <motion.div 
            key={activeCase}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-3"
          >
            {activeStudy.capabilities.map((capability, index) => (
              <div key={index} className="glass-card flex flex-col relative overflow-hidden hover:scale-105 transition-all duration-300">
                <div className="absolute -right-12 -top-12 h-32 w-32 rounded-full bg-gradient-to-br from-primary to-purple-600 opacity-20 blur-2xl" />
                <capability.icon className={`h-8 w-8 ${capability.color}`} />
                
                <h3 className="mt-4 text-xl font-semibold text-white">
                  {capability.category}
                </h3>
                
                {capability.tools.map((tool, toolIndex) => (
                  <div key={toolIndex} className="mt-4">
                    <h4 className="text-lg font-medium text-gray-200">{tool.name}</h4>
                    <p className="mt-2 text-sm text-gray-400">{tool.description}</p>
                    <p className="mt-2 text-sm font-medium text-blue-300/80">{tool.metrics}</p>
                  </div>
                ))}
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}